import { useParams, Link } from 'react-router-dom';
import { webtoons, webtoonCategories } from '@/mocks/webtoons';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const webtoon = webtoons.find((w) => w.id === Number(id));

  if (!webtoon) {
    return <NotFound />;
  }

  const category = webtoonCategories.find((c) => c.slug === webtoon.category);

  const related = webtoons
    .filter((w) => w.id !== webtoon.id && (w.category === webtoon.category || w.tags.some((t) => webtoon.tags.includes(t))))
    .slice(0, 4);

  const pageTitle = `${webtoon.title} - 在线韩漫_韩漫网`;
  const pageDesc = `在线免费阅读${webtoon.title}，${webtoon.author}作品。${webtoon.description.slice(0, 100)}。韩漫网为您提供优质的韩国漫画在线阅读体验。`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: pageTitle,
        description: pageDesc,
        author: {
          '@type': 'Person',
          name: webtoon.author,
        },
        datePublished: webtoon.chapters[0]?.date,
        dateModified: webtoon.chapters[webtoon.chapters.length - 1]?.date,
        image: webtoon.cover,
        publisher: {
          '@type': 'Organization',
          name: '韩漫网',
        },
        url: `https://qmss2.cn/article/${webtoon.id}`,
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <article className="max-w-screen-2xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="mb-4">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(category ? [{ label: `${category.name}漫画`, href: `/category/${category.slug}` }] : []),
              { label: webtoon.title },
            ]}
          />
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-shrink-0">
            <div className="w-48 md:w-56 aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
              <img
                src={webtoon.cover}
                alt={webtoon.title}
                title={webtoon.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
              {webtoon.title}
            </h1>
            <p className="text-sm text-foreground-600 mb-4">
              作者：<strong className="text-foreground-900">{webtoon.author}</strong>
            </p>

            <div className="flex items-center gap-4 flex-wrap mb-4">
              <span className="flex items-center gap-1 text-primary-500">
                <i className="ri-star-fill"></i>
                <span className="font-semibold">{webtoon.rating}</span>
              </span>
              <span className="text-sm text-foreground-600">
                <i className="ri-eye-line mr-1"></i>{webtoon.views} 阅读
              </span>
              <span className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                webtoon.status === '连载中'
                  ? 'bg-accent-100 text-accent-900'
                  : 'bg-secondary-100 text-secondary-900'
              }`}>
                {webtoon.status}
              </span>
            </div>

            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {webtoon.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs bg-background-100 text-foreground-700 rounded-full whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="prose prose-sm max-w-none mb-6">
              <h2 className="font-heading text-lg text-foreground-950 mb-2">作品简介</h2>
              <p className="text-sm text-foreground-700 leading-relaxed">
                {webtoon.description}
              </p>
            </div>
          </div>
        </div>

        <section className="mt-10">
          <h2 className="font-heading text-xl text-foreground-950 mb-4">
            章节列表
            <span className="text-sm text-foreground-600 ml-2 font-sans">
              共 {webtoon.chapters.length} 话
            </span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {webtoon.chapters.map((chapter) => (
              <div
                key={chapter.id}
                className="flex items-center justify-between p-3 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer group"
              >
                <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
                  {chapter.title}
                </span>
                <span className="text-xs text-foreground-500 whitespace-nowrap ml-3">
                  {chapter.date}
                </span>
              </div>
            ))}
          </div>
        </section>

        {related.length > 0 && (
          <section className="mt-10">
            <h2 className="font-heading text-xl text-foreground-950 mb-4">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.id}`}
                  className="group cursor-pointer"
                  data-product-shop
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                    <img
                      src={item.cover}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
                    {item.title}
                  </h3>
                  <p className="text-xs text-foreground-600 mt-1">{item.author}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </>
  );
}